// @noflow
import { makeVar, useReactiveVar } from '@apollo/client'
import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { localeToStripeLocale } from '@/utils/localeToStripeLocale'
import { captureMessage } from '@/utils/sentry'

import BRAND_COLOURS from '@/constants/BrandColours'

import CircleSparksRed from 'assets/images/icons/ornaments/sparks/circle-sparks--red.svg'

import Banner, { bannerMessageState } from '../../components/Banner/Banner'
import { totalAmount } from '../../components/DsAppOrderSummary/DsAppOrderSummary'
import PaymmentOptions from '../../components/PaymentOptions/PaymentOptions'
import Text from '@/components/elements/atoms/Text'
import HeroText from '@/components/elements/organisms/HeroText/HeroText'

import STYLES from './PaymentScreen.module.sass'

import { PaymentPageContext } from '../../PayOnOwnDevicePage'
import { PayOnOwnDevicePageRoutes } from '../../routes/RouterWrapper'

type ActivePaymentViews =
  | 'none'
  | 'creditCard'
  | 'iDeal'
  | 'bancontact'
  | 'paypal'
  | 'googlePay'
  | 'applePay'

type SubmissionState =
  | { type: 'not-requested' }
  | { type: 'loading' }
  | { type: 'error'; error: string }

type LoginUrlProps = {
  signUpUrl: string
  referralUrl: string
}

const TitleAdornment = ({ namespace }: { namespace: string }): JSX.Element => {
  return (
    <>
      <Text
        translate
        namespace={namespace}
        text={'step_two'}
        variant="textMono16"
      />
      <img
        className={STYLES.titleAdornmentImage}
        src={CircleSparksRed}
        alt=""
      />
    </>
  )
}

const paymentSubmissionState = makeVar<SubmissionState>({
  type: 'not-requested'
})

const userLoginUrl = makeVar<LoginUrlProps>({
  signUpUrl: 'https://butternutbox.com/users/sign_in',
  referralUrl: ''
})

const setSubmissionState = (state: SubmissionState): void => {
  paymentSubmissionState({
    ...state
  })
}

const PaymentScreenComponent = ({
  namespace
}: {
  namespace: string
}): JSX.Element | null => {
  const navigate = useNavigate()
  const [activePaymentView, setActivePaymentView] =
    useState<ActivePaymentViews>('none')

  const bannerMessage = useReactiveVar(bannerMessageState)
  const { paymentPageState } = useContext(PaymentPageContext)

  const { amount } = useReactiveVar(totalAmount)
  const storedAmount = parseInt(localStorage.getItem('poyod_amount') || '0')
  const updateLoginUrl = (newProps: Partial<LoginUrlProps>) => {
    const currentProps = userLoginUrl()
    const updatedProps = { ...currentProps, ...newProps }
    userLoginUrl(updatedProps)
  }

  const navigateToNextStep = useCallback(
    ({ signUpUrl, referralUrl }: LoginUrlProps) => {
      updateLoginUrl({
        signUpUrl: signUpUrl,
        referralUrl: referralUrl
      })
      navigate(PayOnOwnDevicePageRoutes.completeScreen)
    },
    [navigate]
  )

  if (!paymentPageState || Math.max(amount, storedAmount) === 0) {
    return null
  }

  return (
    <div className={STYLES.titleAdornment}>
      <HeroText
        namespace={namespace}
        subtitle={`plan_steps.payment.header.title`}
        variant="plans"
        topExtras={<TitleAdornment namespace={namespace} />}
      />
      <PaymmentOptions
        activePaymentView={activePaymentView}
        setActivePaymentView={setActivePaymentView}
        navigateToNextStep={navigateToNextStep}
        namespace={namespace}
      />
      {bannerMessage.message && (
        <Banner namespace={namespace} message={bannerMessage.message} />
      )}
    </div>
  )
}

const PaymentScreen = ({
  namespace
}: {
  namespace: string
}): JSX.Element | null => {
  const { amount } = useReactiveVar(totalAmount)
  const { paymentPageState } = useContext(PaymentPageContext)
  useEffect(() => {
    if (amount && paymentPageState) {
      const userId =
        paymentPageState?.data?.directSalesPendingSubscription
          ?.pendingSubscription?.user?.id
      // eslint-disable-next-line i18next/no-literal-string
      captureMessage(`Direct Sales Payment screen loaded for user ${userId}`, {
        extra: {
          planId:
            paymentPageState?.data?.directSalesPendingSubscription
              ?.pendingSubscription?.plan?.id,
          userId:
            paymentPageState?.data?.directSalesPendingSubscription
              ?.pendingSubscription?.user?.id,
          productVariantIds:
            paymentPageState?.data?.directSalesPendingSubscription?.pendingSubscription?.productVariants?.map(
              (variant) => variant?.productVariant?.id
            ),
          discountCodeId:
            paymentPageState?.data?.directSalesPendingSubscription?.discountCode
              ?.id,
          shippingCountryCode:
            paymentPageState?.data?.directSalesPendingSubscription?.address
              ?.shippingCountry?.code,
          amount: amount
        },
        logLevel: 'info'
      })
    }
  }, [amount, paymentPageState])

  if (!paymentPageState) {
    return null
  }

  const { language, stripeKey, data } = paymentPageState
  const {
    directSalesPendingSubscription: { address }
  } = data
  const { shippingCountry } = address
  const storedAmount = parseFloat(localStorage.getItem('poyod_amount') || '0')

  const stripePromise = loadStripe(stripeKey)

  const effectiveAmount = amount || storedAmount

  if (!effectiveAmount || effectiveAmount === 0) {
    return null
  }

  return (
    <Elements
      stripe={stripePromise}
      key={stripeKey}
      options={{
        locale: localeToStripeLocale(language),
        mode: 'payment',
        amount: effectiveAmount,
        currency: shippingCountry.currency?.toLowerCase(),
        setupFutureUsage: 'off_session',
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: BRAND_COLOURS.brandBlue500,
            colorText: BRAND_COLOURS.brandBlue500,
            fontFamily: 'gt-pressura-regular, helvetica, arial, sans-serif',
            fontWeightMedium: '400',
            fontSizeBase: '18px'
          },
          rules: {
            '.AccordionItem': {
              borderColor: BRAND_COLOURS.brandBlue500,
              boxShadow: 'none',
              borderWidth: '0 0 0.1rem',
              borderRadius: '0'
            },
            '.RadioIconOuter': {
              stroke: BRAND_COLOURS.brandBlue500
            },
            '.Label': {
              color: BRAND_COLOURS.brandBlue500
            },
            '.Input': {
              borderColor: BRAND_COLOURS.brandBlue500
            }
          }
        },
        fonts: [
          {
            family: 'gt-pressura-regular',
            src: 'url(https://cdn.butternutbox.com/assets/GT-Pressura-Regular-939a820c2e8d519892d754e9b7e4b3a6ee751b6c1620f5794f89a27152f1cace.woff2)',
            weight: '400'
          }
        ]
      }}
    >
      <PaymentScreenComponent namespace={namespace} />
    </Elements>
  )
}

export {
  paymentSubmissionState,
  setSubmissionState,
  ActivePaymentViews,
  LoginUrlProps,
  userLoginUrl
}

export default PaymentScreen
